import React from "react";
import { useNavigate } from "react-router-dom";
import { userLogout } from "../../common/service/CommonService";
import styles from "../../assets/scss/common/noStudyPlaceholder.module.scss";

interface NoStudySelectedPlaceholderProps {
  moduleName?: string;
  whyRequiredText?: string;
}

const NoStudySelectedPlaceholder: React.FC<NoStudySelectedPlaceholderProps> = ({
  moduleName = "labels",
  whyRequiredText,
}) => {
  const navigate = useNavigate();

  const handleSignOut = () => {
    userLogout(navigate);
  };
  return (
    <div className={styles.root}>

      {/* ═══ Background Decorations ═══ */}

      {/* Top-Left DNA Helix */}
      <div className={`${styles.bgDecoration} ${styles.dnaTopLeft}`}>
        <svg width="100" height="220" viewBox="0 0 100 240" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 10 C35 30, 65 30, 80 50 C95 70, 95 90, 80 110 C65 130, 35 130, 20 150 C5 170, 5 190, 20 210 C35 230, 65 230, 80 250" stroke="#7C3AED" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M80 10 C65 30, 35 30, 20 50 C5 70, 5 90, 20 110 C35 130, 65 130, 80 150 C95 170, 95 190, 80 210 C65 230, 35 230, 20 250" stroke="#818CF8" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="31" y1="30" x2="69" y2="30" stroke="#A5B4FC" strokeWidth="1.5" />
          <line x1="48" y1="50" x2="52" y2="50" stroke="#A5B4FC" strokeWidth="1.5" />
          <line x1="31" y1="70" x2="69" y2="70" stroke="#A5B4FC" strokeWidth="1.5" />
          <line x1="20" y1="90" x2="80" y2="90" stroke="#A5B4FC" strokeWidth="1.5" />
          <line x1="31" y1="110" x2="69" y2="110" stroke="#A5B4FC" strokeWidth="1.5" />
          <line x1="48" y1="130" x2="52" y2="130" stroke="#A5B4FC" strokeWidth="1.5" />
          <line x1="31" y1="150" x2="69" y2="150" stroke="#A5B4FC" strokeWidth="1.5" />
          <line x1="20" y1="170" x2="80" y2="170" stroke="#A5B4FC" strokeWidth="1.5" />
          <line x1="31" y1="190" x2="69" y2="190" stroke="#A5B4FC" strokeWidth="1.5" />
          <line x1="48" y1="210" x2="52" y2="210" stroke="#A5B4FC" strokeWidth="1.5" />
        </svg>
      </div>

      {/* Top-Left Floating Plus */}
      <div className={`${styles.bgDecoration} ${styles.plusTopLeft}`}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 4V20M4 12H20" stroke="#7C3AED" strokeWidth="3" strokeLinecap="round" />
        </svg>
      </div>

      {/* Top-Right Molecular Structure */}
      <div className={`${styles.bgDecoration} ${styles.moleculeTopRight}`}>
        <svg width="220" height="220" viewBox="0 0 280 280" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="140" cy="50" r="10" stroke="#7C3AED" strokeWidth="2.5" />
          <circle cx="50" cy="120" r="8" stroke="#818CF8" strokeWidth="2.5" />
          <circle cx="230" cy="120" r="8" stroke="#818CF8" strokeWidth="2.5" />
          <circle cx="100" cy="200" r="12" stroke="#7C3AED" strokeWidth="2.5" />
          <circle cx="180" cy="200" r="10" stroke="#7C3AED" strokeWidth="2.5" />
          <line x1="133" y1="58" x2="57" y2="112" stroke="#A5B4FC" strokeWidth="2" />
          <line x1="147" y1="58" x2="223" y2="112" stroke="#A5B4FC" strokeWidth="2" />
          <line x1="58" y1="124" x2="92" y2="188" stroke="#A5B4FC" strokeWidth="2" />
          <line x1="222" y1="124" x2="188" y2="188" stroke="#A5B4FC" strokeWidth="2" />
          <line x1="112" y1="200" x2="168" y2="200" stroke="#A5B4FC" strokeWidth="2" />
        </svg>
      </div>

      {/* Bottom-Left Molecules & Cross */}
      <div className={`${styles.bgDecoration} ${styles.moleculeBottomLeft}`}>
        <svg width="160" height="160" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="40" cy="160" r="8" stroke="#818CF8" strokeWidth="2" />
          <circle cx="120" cy="100" r="10" stroke="#7C3AED" strokeWidth="2" />
          <circle cx="160" cy="160" r="6" stroke="#818CF8" strokeWidth="2" />
          <line x1="47" y1="153" x2="113" y2="107" stroke="#A5B4FC" strokeWidth="1.5" />
          <line x1="126" y1="108" x2="154" y2="152" stroke="#A5B4FC" strokeWidth="1.5" />
          <path d="M48 44v28M34 58h28" stroke="#7C3AED" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </div>

      {/* Bottom-Right Dot Grid */}
      <div className={`${styles.bgDecoration} ${styles.dotGridBottomRight}`}>
        <svg width="100" height="100" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          {[20, 45, 70, 95].map(cy =>
            [20, 45, 70, 95].map(cx => (
              <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="2.5" fill="#7C3AED" />
            ))
          )}
        </svg>
      </div>

      {/* ═══ MAIN CONTENT ═══ */}
      <div className={styles.contentWrapper}>

        {/* ── Hero Illustration ── */}
        <div className={styles.illustrationContainer}>
          <svg width="100%" height="100%" viewBox="0 0 960 430" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              {/* ── Ambient glow ── */}
              <radialGradient id="bg-glow" cx="480" cy="210" r="280" fx="480" fy="210" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#DDE0FF" stopOpacity="0.85" />
                <stop offset="40%" stopColor="#E8EBFF" stopOpacity="0.5" />
                <stop offset="70%" stopColor="#F0F1FF" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#F7F8FC" stopOpacity="0" />
              </radialGradient>

              {/* ── Mortar body ── */}
              <linearGradient id="mortar-body" x1="390" y1="180" x2="570" y2="340" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#FFFFFF" />
                <stop offset="20%" stopColor="#F5F6FF" />
                <stop offset="50%" stopColor="#E0E3FC" />
                <stop offset="80%" stopColor="#C8CCF5" />
                <stop offset="100%" stopColor="#B0B6EE" />
              </linearGradient>
              <linearGradient id="mortar-inner" x1="420" y1="195" x2="540" y2="240" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#B0B6EE" />
                <stop offset="50%" stopColor="#8E96DF" />
                <stop offset="100%" stopColor="#6E76CF" />
              </linearGradient>

              {/* ── Pestle ── */}
              <linearGradient id="pestle-grad" x1="0" y1="0" x2="1" y2="1" gradientUnits="objectBoundingBox">
                <stop offset="0%" stopColor="#FFFFFF" />
                <stop offset="30%" stopColor="#EEF0FF" />
                <stop offset="65%" stopColor="#D0D4F8" />
                <stop offset="100%" stopColor="#A0A8ED" />
              </linearGradient>

              {/* ── Books ── */}
              <linearGradient id="book-top" x1="0" y1="0" x2="1" y2="1" gradientUnits="objectBoundingBox">
                <stop offset="0%" stopColor="#8488F5" />
                <stop offset="50%" stopColor="#6A6EEB" />
                <stop offset="100%" stopColor="#4448C0" />
              </linearGradient>
              <linearGradient id="book-bottom" x1="0" y1="0" x2="1" y2="1" gradientUnits="objectBoundingBox">
                <stop offset="0%" stopColor="#7074EA" />
                <stop offset="50%" stopColor="#5558D0" />
                <stop offset="100%" stopColor="#3638A0" />
              </linearGradient>
              <linearGradient id="book-pages" x1="0" y1="0" x2="0" y2="1" gradientUnits="objectBoundingBox">
                <stop offset="0%" stopColor="#FAFBFF" />
                <stop offset="100%" stopColor="#E8EBFC" />
              </linearGradient>

              {/* ── Blister pack ── */}
              <linearGradient id="blister-foil" x1="0" y1="0" x2="1" y2="1" gradientUnits="objectBoundingBox">
                <stop offset="0%" stopColor="#FFFFFF" />
                <stop offset="40%" stopColor="#EEF1FE" />
                <stop offset="100%" stopColor="#C4CEF8" />
              </linearGradient>
              <radialGradient id="pill-dome" cx="0.35" cy="0.3" r="0.7" gradientUnits="objectBoundingBox">
                <stop offset="0%" stopColor="#FFFFFF" />
                <stop offset="35%" stopColor="#E2E6FD" />
                <stop offset="100%" stopColor="#97A5EB" />
              </radialGradient>

              {/* ── Bottle ── */}
              <linearGradient id="bottle-body" x1="0" y1="0" x2="0.3" y2="1" gradientUnits="objectBoundingBox">
                <stop offset="0%" stopColor="#7578F6" />
                <stop offset="35%" stopColor="#5C5FE8" />
                <stop offset="70%" stopColor="#4245CC" />
                <stop offset="100%" stopColor="#2F31A6" />
              </linearGradient>
              <linearGradient id="bottle-cap" x1="0" y1="0" x2="0" y2="1" gradientUnits="objectBoundingBox">
                <stop offset="0%" stopColor="#6568F0" />
                <stop offset="100%" stopColor="#4A4DC8" />
              </linearGradient>

              {/* ── Flask ── */}
              <linearGradient id="flask-glass" x1="0" y1="0" x2="0.3" y2="1" gradientUnits="objectBoundingBox">
                <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.92" />
                <stop offset="25%" stopColor="#EEF2FF" stopOpacity="0.7" />
                <stop offset="60%" stopColor="#DAE0FF" stopOpacity="0.45" />
                <stop offset="100%" stopColor="#B8C4FA" stopOpacity="0.85" />
              </linearGradient>
              <linearGradient id="flask-liquid" x1="0" y1="0" x2="0.2" y2="1" gradientUnits="objectBoundingBox">
                <stop offset="0%" stopColor="#9498F8" />
                <stop offset="50%" stopColor="#7074EA" />
                <stop offset="100%" stopColor="#5054D8" />
              </linearGradient>

              {/* ── Capsule halves ── */}
              <linearGradient id="capsule-purple" x1="0" y1="0" x2="0" y2="1" gradientUnits="objectBoundingBox">
                <stop offset="0%" stopColor="#7C7FF5" />
                <stop offset="100%" stopColor="#5558D8" />
              </linearGradient>
              <linearGradient id="capsule-light" x1="0" y1="0" x2="0" y2="1" gradientUnits="objectBoundingBox">
                <stop offset="0%" stopColor="#FFFFFF" />
                <stop offset="100%" stopColor="#E8EBFC" />
              </linearGradient>

              {/* ── Soft drop shadow filters ── */}
              <filter id="shadow-soft" x="-20%" y="-10%" width="140%" height="140%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="6" />
                <feOffset dx="0" dy="4" />
                <feColorMatrix values="0 0 0 0 0.55  0 0 0 0 0.55  0 0 0 0 0.85  0 0 0 0.2 0" />
                <feMerge>
                  <feMergeNode />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="shadow-tight" x="-15%" y="-8%" width="130%" height="130%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
                <feOffset dx="0" dy="2" />
                <feColorMatrix values="0 0 0 0 0.5  0 0 0 0 0.5  0 0 0 0 0.8  0 0 0 0.18 0" />
                <feMerge>
                  <feMergeNode />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <clipPath id="pestle-clip">
                <rect x="0" y="0" width="960" height="200" />
                <ellipse cx="480" cy="200" rx="78" ry="10" />
              </clipPath>
            </defs>

            {/* ══════ BACKGROUND ══════ */}
            <circle cx="480" cy="215" r="260" fill="url(#bg-glow)" />

            {/* Faint DNA helix – left background */}
            <g opacity="0.18" transform="translate(120, 60)">
              <path d="M12 10 Q30 45, 12 80 T12 150 T12 220 T12 280" fill="none" stroke="#9EA3F1" strokeWidth="2.2" strokeLinecap="round" />
              <path d="M42 10 Q24 45, 42 80 T42 150 T42 220 T42 280" fill="none" stroke="#9EA3F1" strokeWidth="2.2" strokeLinecap="round" />
              <line x1="18" y1="28" x2="36" y2="28" stroke="#B4BAFC" strokeWidth="1.2" />
              <line x1="20" y1="62" x2="34" y2="62" stroke="#B4BAFC" strokeWidth="1.2" />
              <line x1="14" y1="95" x2="40" y2="95" stroke="#B4BAFC" strokeWidth="1.2" />
              <line x1="18" y1="128" x2="36" y2="128" stroke="#B4BAFC" strokeWidth="1.2" />
              <line x1="20" y1="162" x2="34" y2="162" stroke="#B4BAFC" strokeWidth="1.2" />
              <line x1="14" y1="195" x2="40" y2="195" stroke="#B4BAFC" strokeWidth="1.2" />
              <line x1="18" y1="228" x2="36" y2="228" stroke="#B4BAFC" strokeWidth="1.2" />
              <line x1="20" y1="262" x2="34" y2="262" stroke="#B4BAFC" strokeWidth="1.2" />
            </g>

            {/* Benzene ring – molecular structure – right background */}
            <g opacity="0.2" stroke="#9EA3F1" strokeWidth="1.8" transform="translate(720, 80)">
              <circle cx="40" cy="0" r="6" fill="#E4E6FD" />
              <circle cx="80" cy="0" r="6" fill="#E4E6FD" />
              <circle cx="100" cy="35" r="6" fill="#E4E6FD" />
              <circle cx="80" cy="70" r="6" fill="#E4E6FD" />
              <circle cx="40" cy="70" r="6" fill="#E4E6FD" />
              <circle cx="20" cy="35" r="6" fill="#E4E6FD" />
              <line x1="46" y1="0" x2="74" y2="0" />
              <line x1="84" y1="5" x2="96" y2="30" />
              <line x1="96" y1="40" x2="84" y2="65" />
              <line x1="74" y1="70" x2="46" y2="70" />
              <line x1="36" y1="65" x2="24" y2="40" />
              <line x1="24" y1="30" x2="36" y2="5" />
              <line x1="50" y1="5" x2="70" y2="5" strokeDasharray="3 2" />
              <line x1="50" y1="65" x2="70" y2="65" strokeDasharray="3 2" />
              <circle cx="0" cy="35" r="4" fill="#E4E6FD" />
              <line x1="16" y1="35" x2="4" y2="35" />
              <circle cx="120" cy="35" r="4" fill="#E4E6FD" />
              <line x1="104" y1="35" x2="116" y2="35" />
            </g>

            {/* Medical crosses */}
            <g opacity="0.22" transform="translate(210, 75)">
              <rect x="7" y="0" width="7" height="21" rx="2" fill="#7C3AED" />
              <rect x="0" y="7" width="21" height="7" rx="2" fill="#7C3AED" />
            </g>
            <g opacity="0.18" transform="translate(665, 90)">
              <rect x="5" y="0" width="6" height="16" rx="1.5" fill="#7C3AED" />
              <rect x="0" y="5" width="16" height="6" rx="1.5" fill="#7C3AED" />
            </g>
            <g opacity="0.12" transform="translate(190, 290)">
              <rect x="4" y="0" width="5" height="13" rx="1.5" fill="#7C3AED" />
              <rect x="0" y="4" width="13" height="5" rx="1.5" fill="#7C3AED" />
            </g>

            {/* 4-point sparkles */}
            <path d="M250 200 L252 192 L260 190 L252 188 L250 180 L248 188 L240 190 L248 192Z" fill="#A5B4FC" opacity="0.45" />
            <path d="M400 68 L402 60 L409 58 L402 56 L400 48 L398 56 L391 58 L398 60Z" fill="#7C3AED" opacity="0.35" />
            <path d="M590 95 L592 88 L598 86 L592 84 L590 77 L588 84 L582 86 L588 88Z" fill="#818CF8" opacity="0.5" />
            <path d="M760 210 L761 206 L765 205 L761 204 L760 200 L759 204 L755 205 L759 206Z" fill="#A5B4FC" opacity="0.3" />

            {/* Floating capsule outline */}
            <g opacity="0.2" transform="translate(640, 115) rotate(35) scale(0.75)">
              <rect x="0" y="0" width="34" height="16" rx="8" stroke="#7C3AED" strokeWidth="2.5" fill="none" />
              <line x1="17" y1="0" x2="17" y2="16" stroke="#7C3AED" strokeWidth="2" />
            </g>

            {/* ══════ FLOOR / TABLE ══════ */}
            <ellipse cx="480" cy="340" rx="310" ry="18" fill="#D8DCFA" opacity="0.7" />

            {/* ══════ LEFT: BOOKS + BLISTER PACK ══════ */}
            <g filter="url(#shadow-soft)">
              <ellipse cx="310" cy="330" rx="90" ry="12" fill="#CBCEF8" opacity="0.6" />

              {/* Bottom book */}
              <g>
                <path d="M235 260 L350 234 C355 233, 359 236, 359 240 L359 270 C359 274, 355 278, 350 279 L235 305 C230 306, 226 303, 226 299 L226 268 C226 264, 230 261, 235 260Z" fill="url(#book-bottom)" />
                <path d="M350 234 L408 222 L408 260 L350 272Z" fill="url(#book-pages)" />
                <line x1="356" y1="238" x2="356" y2="268" stroke="#D8DDFB" strokeWidth="0.5" />
                <line x1="362" y1="236" x2="362" y2="266" stroke="#D8DDFB" strokeWidth="0.5" />
                <line x1="368" y1="234" x2="368" y2="264" stroke="#D8DDFB" strokeWidth="0.5" />
                <line x1="374" y1="232" x2="374" y2="262" stroke="#D8DDFB" strokeWidth="0.5" />
                <line x1="380" y1="230" x2="380" y2="260" stroke="#D8DDFB" strokeWidth="0.5" />
                <line x1="386" y1="228" x2="386" y2="258" stroke="#D8DDFB" strokeWidth="0.5" />
                <line x1="392" y1="226" x2="392" y2="256" stroke="#D8DDFB" strokeWidth="0.5" />
                <line x1="398" y1="224" x2="398" y2="254" stroke="#D8DDFB" strokeWidth="0.5" />
                <path d="M350 272 L408 260 C410 260, 412 262, 412 265 L412 267 L350 279Z" fill="#F0F2FE" />
                <path d="M235 305 L350 279 L412 267" stroke="#313393" strokeWidth="1.8" strokeLinecap="round" />
                <text x="290" y="278" transform="rotate(-13 290 278)" fill="#FFFFFF" fillOpacity="0.8" fontSize="8" fontWeight="700" fontFamily="sans-serif" letterSpacing="0.9">PHARMACY PRACTICE</text>
              </g>

              {/* Top book */}
              <g>
                <path d="M240 222 L355 196 C360 195, 364 198, 364 202 L364 228 C364 232, 360 236, 355 237 L240 263 C235 264, 231 261, 231 257 L231 230 C231 226, 235 223, 240 222Z" fill="url(#book-top)" />
                <path d="M355 196 L413 184 L413 218 L355 230Z" fill="url(#book-pages)" />
                <line x1="361" y1="200" x2="361" y2="226" stroke="#D8DDFB" strokeWidth="0.5" />
                <line x1="367" y1="198" x2="367" y2="224" stroke="#D8DDFB" strokeWidth="0.5" />
                <line x1="373" y1="196" x2="373" y2="222" stroke="#D8DDFB" strokeWidth="0.5" />
                <line x1="379" y1="194" x2="379" y2="220" stroke="#D8DDFB" strokeWidth="0.5" />
                <line x1="385" y1="192" x2="385" y2="218" stroke="#D8DDFB" strokeWidth="0.5" />
                <line x1="391" y1="190" x2="391" y2="216" stroke="#D8DDFB" strokeWidth="0.5" />
                <line x1="397" y1="188" x2="397" y2="214" stroke="#D8DDFB" strokeWidth="0.5" />
                <line x1="403" y1="186" x2="403" y2="212" stroke="#D8DDFB" strokeWidth="0.5" />
                <path d="M355 230 L413 218 C415 218, 417 220, 417 223 L417 224 L355 236Z" fill="#F0F2FE" />
                <path d="M240 263 L355 237 L417 224" stroke="#3A3C9E" strokeWidth="1.8" strokeLinecap="round" />
                <text x="295" y="236" transform="rotate(-13 295 236)" fill="#FFFFFF" fillOpacity="0.9" fontSize="9" fontWeight="700" fontFamily="sans-serif" letterSpacing="1">PHARMACOLOGY</text>
                <path d="M242 228 Q245 222, 250 225" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
              </g>
            </g>

            {/* ── Blister pack on top of books ── */}
            <g filter="url(#shadow-tight)" transform="translate(290, 155) rotate(42) scale(0.9)">
              <rect x="0" y="0" width="60" height="100" rx="8" fill="url(#blister-foil)" stroke="#C3CDF8" strokeWidth="1.5" />
              <circle cx="18" cy="20" r="8" fill="url(#pill-dome)" />
              <circle cx="42" cy="20" r="8" fill="url(#pill-dome)" />
              <circle cx="18" cy="50" r="8" fill="url(#pill-dome)" />
              <circle cx="42" cy="50" r="8" fill="url(#pill-dome)" />
              <circle cx="18" cy="80" r="8" fill="url(#pill-dome)" />
              <circle cx="42" cy="80" r="8" fill="url(#pill-dome)" />
              <circle cx="15" cy="17" r="2.5" fill="#FFFFFF" opacity="0.7" />
              <circle cx="39" cy="17" r="2.5" fill="#FFFFFF" opacity="0.7" />
              <circle cx="15" cy="47" r="2.5" fill="#FFFFFF" opacity="0.7" />
              <circle cx="39" cy="47" r="2.5" fill="#FFFFFF" opacity="0.7" />
              <circle cx="15" cy="77" r="2.5" fill="#FFFFFF" opacity="0.7" />
              <circle cx="39" cy="77" r="2.5" fill="#FFFFFF" opacity="0.7" />
              <line x1="30" y1="5" x2="30" y2="95" stroke="#A5B4FC" strokeWidth="0.8" strokeDasharray="3 3" />
              <line x1="5" y1="35" x2="55" y2="35" stroke="#A5B4FC" strokeWidth="0.8" strokeDasharray="3 3" />
              <line x1="5" y1="65" x2="55" y2="65" stroke="#A5B4FC" strokeWidth="0.8" strokeDasharray="3 3" />
            </g>

            {/* ══════ CENTER: MORTAR & PESTLE ══════ */}
            <g filter="url(#shadow-soft)">
              <ellipse cx="480" cy="335" rx="68" ry="14" fill="#B0B6EE" opacity="0.75" />
              <path d="M440 318 C440 318, 448 335, 480 335 C512 335, 520 318, 520 318Z" fill="url(#mortar-body)" />
              <path d="M388 200 C388 270, 420 322, 480 322 C540 322, 572 270, 572 200 C572 215, 535 228, 480 228 C425 228, 388 215, 388 200Z" fill="url(#mortar-body)" />
              <ellipse cx="480" cy="200" rx="92" ry="16" fill="url(#mortar-body)" stroke="#D4D9FB" strokeWidth="1.2" />
              <path d="M400 196 Q440 188, 480 186 Q520 188, 560 196" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.6" />
              <ellipse cx="480" cy="200" rx="78" ry="10" fill="url(#mortar-inner)" />
              <ellipse cx="480" cy="203" rx="72" ry="6" fill="#6E76CF" opacity="0.5" />
              <path d="M398 220 Q402 260, 420 305" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" opacity="0.2" />

              {/* Pestle */}
              <g clipPath="url(#pestle-clip)">
                <g transform="translate(520, 128) rotate(30)">
                  <ellipse cx="16" cy="108" rx="12" ry="5" fill="#7078C8" opacity="0.25" />
                  <rect x="3" y="12" width="26" height="100" rx="13" fill="url(#pestle-grad)" />
                  <circle cx="16" cy="14" r="16" fill="url(#pestle-grad)" />
                  <path d="M8 14 C8 6, 15 3, 20 3" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" opacity="0.55" />
                  <path d="M8 30 L8 90" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" opacity="0.2" />
                </g>
              </g>
            </g>

            {/* ══════ RIGHT: BOTTLE + FLASK + CAPSULES ══════ */}

            {/* ── Medicine Bottle ── */}
            <g filter="url(#shadow-soft)">
              <ellipse cx="640" cy="328" rx="35" ry="9" fill="#B0B6EE" opacity="0.7" />
              <rect x="604" y="196" width="72" height="120" rx="16" fill="url(#bottle-body)" />
              <rect x="604" y="222" width="72" height="58" fill="#FFFFFF" fillOpacity="0.95" />
              <rect x="614" y="232" width="52" height="6" rx="1" fill="#6568F0" />
              <rect x="614" y="244" width="38" height="4" rx="1" fill="#A5B4FC" />
              <rect x="614" y="254" width="44" height="4" rx="1" fill="#A5B4FC" />
              <rect x="614" y="264" width="30" height="4" rx="1" fill="#C5CAF7" />
              <rect x="654" y="244" width="2.5" height="10" rx="0.5" fill="#EF4444" />
              <rect x="650" y="248" width="10" height="2.5" rx="0.5" fill="#EF4444" />
              <path d="M620 196 L660 196 L660 184 L620 184Z" fill="#5C5FE8" />
              <rect x="612" y="168" width="56" height="18" rx="4" fill="url(#bottle-cap)" />
              {[618, 624, 630, 636, 642, 648, 654, 660].map(x => (
                <line key={`cap-${x}`} x1={x} y1="170" x2={x} y2="184" stroke="#5558D0" strokeWidth="1.2" opacity="0.4" />
              ))}
              <path d="M612 210 Q616 240, 614 305" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" opacity="0.25" />
            </g>

            {/* ── Laboratory Flask (Erlenmeyer) ── */}
            <g filter="url(#shadow-soft)">
              <ellipse cx="740" cy="330" rx="42" ry="10" fill="#B0B6EE" opacity="0.6" />
              <path d="M740 240 L702 315 C696 327, 705 330, 716 330 L764 330 C775 330, 784 327, 778 315 L740 240Z" fill="url(#flask-liquid)" />
              <ellipse cx="740" cy="240" rx="20" ry="5" fill="#7C3AED" opacity="0.6" />
              <ellipse cx="740" cy="240" rx="16" ry="3.5" fill="#A5B4FC" opacity="0.4" />
              <path d="M734 186 L734 168 H746 V186Z" fill="url(#flask-glass)" />
              <path d="M740 186 L694 310 C686 326, 696 330, 710 330 L770 330 C784 330, 794 326, 786 310 L740 186Z" fill="url(#flask-glass)" stroke="#D4D9FB" strokeWidth="1.8" />
              <ellipse cx="740" cy="166" rx="9" ry="3" fill="#EEF0FF" stroke="#A9B0F0" strokeWidth="1.2" />
              <path d="M712 312 C706 298, 730 218, 735 200" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" opacity="0.4" />
              <circle cx="728" cy="275" r="4" fill="#E0E2FD" opacity="0.65" />
              <circle cx="752" cy="290" r="3" fill="#E0E2FD" opacity="0.75" />
              <circle cx="736" cy="305" r="4.5" fill="#E0E2FD" opacity="0.55" />
              <circle cx="748" cy="260" r="2" fill="#E0E2FD" opacity="0.7" />
              <circle cx="722" cy="295" r="2.5" fill="#E0E2FD" opacity="0.6" />
            </g>

            {/* ── Capsule pills in foreground ── */}
            <g filter="url(#shadow-tight)" transform="translate(600, 315) rotate(-18)">
              <rect x="0" y="0" width="36" height="16" rx="8" fill="url(#capsule-light)" stroke="#CCD1FC" strokeWidth="0.8" />
              <path d="M8 0 h10 v16 h-10 a8 8 0 0 1-8-8 a8 8 0 0 1 8-8z" fill="url(#capsule-purple)" />
              <path d="M4 4 L14 4" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
            </g>
            <g filter="url(#shadow-tight)" transform="translate(660, 320) rotate(25)">
              <rect x="0" y="0" width="36" height="16" rx="8" fill="url(#capsule-light)" stroke="#CCD1FC" strokeWidth="0.8" />
              <path d="M18 0 h10 a8 8 0 0 1 8 8 a8 8 0 0 1-8 8 h-10z" fill="url(#capsule-purple)" />
              <path d="M22 4 L32 4" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
            </g>
            <g filter="url(#shadow-tight)" transform="translate(710, 325) rotate(-8)">
              <rect x="0" y="0" width="32" height="14" rx="7" fill="url(#capsule-light)" stroke="#CCD1FC" strokeWidth="0.8" />
              <path d="M7 0 h9 v14 h-9 a7 7 0 0 1-7-7 a7 7 0 0 1 7-7z" fill="#878BF3" />
              <path d="M3 4 L12 4" stroke="#FFFFFF" strokeWidth="1.3" strokeLinecap="round" opacity="0.45" />
            </g>

          </svg>
        </div>

        {/* ── Heading Content ── */}
        <div className={styles.headingSection}>
          <h1 className={styles.title}>Study Not Assigned</h1>
          <div className={styles.accentDivider} />
          <p className={styles.description}>
            This section requires a selected study to continue.<br />
            <span className={styles.descriptionSecondary}>
              {whyRequiredText ?? `${moduleName.charAt(0).toUpperCase() + moduleName.slice(1)} are linked to a specific study.`}
            </span>
          </p>
        </div>

        {/* ── Action Section & Sign Out Button ── */}
        <div className={styles.actionSection}>
          <button
            type="button"
            className={styles.signOutButton}
            onClick={handleSignOut}
          >
            <i className="ri-logout-box-r-line" /> Sign out
          </button>
        </div>

      </div>
    </div>
  );
};

export default NoStudySelectedPlaceholder;
